import { BaseShow } from '@/components/show';
import { DataDisplay } from '@/components/show/data-display';
import useLayout from '@/hooks/useLayout';
import { useOrder } from '@/modules/order/application/order';
import { convertNumberToCurrency } from '@/utility/helper/common';
import { Typography } from '@rrexpress/core';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useAwb } from '@/modules/check-awb/application/check-awb';
import { NULL_DATE } from '@/utility/helper/dateUtils';
import { AwbHistory } from './components/awb-history';

export function OrderDetailPage() {
  useLayout({ title: 'Order' });
  const { id } = useParams<{ id: string }>();
  const { getOrderById, getPOD } = useOrder();
  const { checkAwb } = useAwb();
  const { data, isLoading } = getOrderById(id as string);
  const order: OrderResponse = (data?.data || {}) as OrderResponse;
  const { data: dataAwb } = checkAwb(order?.awb_number);
  const awbData: ShipmentDetail = (dataAwb?.data || {}) as ShipmentDetail;

  return (
    <BaseShow
      resource="orders"
      isLoading={isLoading}
      title={`Order : ${order.order_number}`}
    >
      <div className="px-6 py-4">

        <div className="grid grid-cols-2 gap-x-8 mb-6">
          <div className="flex flex-col gap-y-4 mb-8">
            <DataDisplay
              label="No Resi"
              value={order.awb_number}
            />
            <DataDisplay
              label="No Order"
              value={order.order_number}
            />
            <DataDisplay
              label="Tgl Order"
              value={dayjs(order.order_date_time_utc).format('DD/MM/YYYY')}
            />
            <DataDisplay
              label="Cabang"
              value={order.branch_name}
            />
          </div>
          <div className="flex flex-col gap-y-4">
            <DataDisplay
              label="Metode"
              value={(order.order_method === 'PICK' || order.order_method === 'pickup') ? 'Pickup' : 'Drop off'}
            />
            {(order.order_method === 'PICK' || order.order_method === 'pickup') && (
              <>
                <DataDisplay
                  label="Tanggal Request Pickup"
                  value={awbData.order_pickup_schedule_date_time_utc !== NULL_DATE ? dayjs(awbData.order_pickup_schedule_date_time_utc).format('DD/MM/YYYY HH:mm') : ''}
                />
                <DataDisplay
                  label="Tanggal Pickup"
                  value={awbData.picked_date_time_utc !== NULL_DATE ? dayjs(awbData.picked_date_time_utc).format('DD/MM/YYYY HH:mm') : ''}
                />
              </>
            )}
            <DataDisplay
              label="Layanan"
              value={order.product_service_code}
            />
            <DataDisplay
              label="Status Terakhir"
              value={`${order.awb_status_name} - ${dayjs(order.updated_time_utc).format('DD/MM/YYYY HH:mm')}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-8 mb-6">
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Pengirim</Typography>
            </div>
            <div className="flex flex-col gap-y-4 mb-8">
              <DataDisplay
                label="Nama"
                value={order.shipper_name}
              />
              <DataDisplay
                label="No Telp"
                value={order.shipper_phone_number}
              />
              <DataDisplay
                label="Alamat Pickup"
                value={order.shipper_pickup_address}
              />
              <DataDisplay
                label="Kota / Kecamatan"
                value={`${order.shipper_pickup_subdistrict_name}, ${order.shipper_pickup_city_name}, ${order.shipper_pickup_province_name}`}
              />
              <DataDisplay
                label="Kelurahan / Kode Pos"
                value={`${order.shipper_pickup_village_name} - ${order.shipper_pickup_zipcode}`}
              />
            </div>
          </div>
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Penerima</Typography>
            </div>
            <div className="flex flex-col gap-y-4">
              <DataDisplay
                label="Nama"
                value={order.consignee_name}
              />
              <DataDisplay
                label="No Telp"
                value={order.consignee_phone_number}
              />
              <DataDisplay
                label="Alamat"
                value={order.consignee_delivery_address}
              />
              <DataDisplay
                label="Kota / Kecamatan"
                value={`${order.consignee_delivery_subdistrict_name}, ${order.consignee_delivery_city_name}, ${order.consignee_delivery_province_name}`}
              />
              <DataDisplay
                label="Kelurahan / Kode Pos"
                value={`${order.consignee_delivery_village_name} - ${order.consignee_delivery_zipcode}`}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-8 mb-6">
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Detail Paket</Typography>
            </div>
            <div className="flex flex-col gap-y-4 mb-8">
              <DataDisplay
                label="Jenis Barang"
                value={order.parcel_type}
              />
              <DataDisplay
                label="Deskripsi Barang"
                value={order.parcel_description}
              />
              <DataDisplay
                label="Berat (Kg)"
                value={`${order.parcel_weight} Kg`}
              />
              <DataDisplay
                label="Asuransi"
                value={`${order.is_insurance ? 'YES' : 'No'}`}
              />
              <DataDisplay
                label="Harga Barang"
                value={convertNumberToCurrency(order.parcel_value)}
              />
            </div>
          </div>
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Rincian Biaya Pengiriman</Typography>
            </div>
            <div className="flex flex-col gap-y-4">
              <DataDisplay
                label="Ongkir"
                value={convertNumberToCurrency(order.price)}
              />
              <DataDisplay
                label="Biaya Asuransi"
                value={convertNumberToCurrency(order.insurance_fee)}
              />
              <DataDisplay
                label="Diskon"
                value={convertNumberToCurrency(order.discount_amount || 0)}
              />
              <DataDisplay
                label="TOTAL"
                value={convertNumberToCurrency(order.total_price || 0)}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-8 mb-6">
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Informasi Lainnya</Typography>
            </div>
            <div className="flex flex-col gap-y-4 mb-8">
              <DataDisplay
                label="Catatan Kurir"
                value={order.courier_note}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-8 mb-6">
          <div className="block mb-4">
            <div className="border-b border-zinc-200 pb-4 mb-6">
              <Typography level={4} fontWeight="bold">Histori Pengiriman</Typography>
            </div>
            <div className="block mb-8">
              <AwbHistory data={awbData} handlePreview={getPOD} />
            </div>
          </div>
        </div>

      </div>
    </BaseShow>
  );
}

export default OrderDetailPage;
